import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button, FormControl } from 'components/common';
import { ApplicationTypeByMembers, IApplicationTypeForm } from './types';
import { ApplicationFormPageEnum } from '../types';
import { applicationTypeQuestions } from './questions';
import { useNavigateBack } from 'hooks';
import { useMutation } from '@tanstack/react-query';
import { useApplicationInProgressById } from 'queries/applications/useApplicationInProgressById';
import { applicationFormApi } from 'api/applicationForm';
import { parseApplicationInProgress } from '../helpers';
import { useEffect } from 'react';
import { getCurrentRoute } from 'utils/location';

export const ApplicationType = () => {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigateBack = useNavigateBack();
  const { id: applicationId = '' } = useParams();
  const currentRoute = getCurrentRoute();
  const { mutate: patchApplicationInProgress } = useMutation(applicationFormApi.patchApplicationInProgress);
  const { data: applicationInProgress } = useApplicationInProgressById(applicationId);
  const formattedResponse =
    applicationInProgress?.[currentRoute] && parseApplicationInProgress(applicationInProgress[currentRoute]);
  const form = useForm<IApplicationTypeForm>({
    defaultValues: {
      applicationType: '',
    },
  });
  const requiredFieldErrorMessage = intl.formatMessage({ id: 'common.error.requiredField' });

  useEffect(() => {
    if (formattedResponse) form.reset(formattedResponse);
  }, [applicationInProgress]);

  const formState = form.watch();
  const isJoined = formState.applicationType === ApplicationTypeByMembers.JOINT_APPLICATION;

  const submitFormHandler = async (formData: IApplicationTypeForm) => {
    const deleteFollowingPages = formattedResponse && formattedResponse.applicationType !== formData.applicationType;
    const questions = Object.entries(formData).map(([key, answer]) => ({
      question: intl.formatMessage({ id: applicationTypeQuestions[key].labelKey }),
      key,
      answer,
    }));

    await patchApplicationInProgress({ questions, currentRoute, applicationId, deleteFollowingPages });

    const nextPage = isJoined ? ApplicationFormPageEnum.RELATIONSHIP : ApplicationFormPageEnum.FULL_NAME;

    if (isJoined) searchParams.set('isJoined', `${isJoined}`);
    searchParams.set('activeApplicant', '1');
    searchParams.set('section', nextPage);
    setSearchParams(searchParams);
  };

  const isAnswersProvided = Object.values(formState).some(value => !!value);

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(submitFormHandler)}>
        <div className='mt-5'>
          <FormattedMessage id={applicationTypeQuestions.applicationType.labelKey} tagName={'h4'} />
          <FormControl
            rules={{ required: requiredFieldErrorMessage }}
            name={'applicationType'}
            radioOptions={applicationTypeQuestions.applicationType.options}
            radioGroupProps={{ className: 'mt-2' }}
          />
        </div>

        <div className={'mt-8 flex justify-between'}>
          <Button variant={'outlined'} onClick={navigateBack}>
            <FormattedMessage id={'cta.back'} />
          </Button>

          <Button variant={'contained'} type={'submit'} disabled={!isAnswersProvided}>
            <FormattedMessage id={'cta.continue'} />
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
